"use client";
import { Button } from "cy-fe";
import { MouseEvent, ReactNode, useState } from "react";
import * as StepWizard from "cy-ui/components/StepWizard";
import { BodyContainer } from "cy-remittance/components/layouts";
import { RadioGroup } from "@headlessui/react";
import { cn } from "cy-fe/src/utils/cn";
import { RoundIconContainer } from "cy-ui/components/RoundIcons";
import { HeaderText } from "cy-remittance/components/HeaderText";

export interface AccountOption {
  id: string;
  name: string;
  icon?: ReactNode;
}

export function AccountOptionsPage<TOption extends AccountOption>({
  options,
  onContinue,
  renderOption,
  backUrl,
  header,
  subHeader,
  initialOption,
}: {
  options: TOption[];
  onContinue: (option: TOption) => void;
  renderOption: (option: TOption) => ReactNode;
  backUrl: string;
  header: string;
  subHeader: string;
  initialOption?: TOption;
}) {
  const [selected, setSelected] = useState<TOption | null>(
    initialOption ?? null,
  );
  const [submitting, setSubmitting] = useState(false);

  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();

    if (!selected) {
      return;
    }

    setSubmitting(true);
    onContinue(selected);
  }

  return (
    <StepWizard.StepPageLayout
      headerRender={() => <HeaderText>{header}</HeaderText>}
      backUrl={backUrl}
      subHeaderRender={() => <>{subHeader}</>}
    >
      <div className="px-5">
        <BodyContainer className="mt-10">
          <RadioGroup onChange={setSelected} value={selected}>
            {options.map((option) => (
              <RadioGroup.Option
                className={({ active, checked }) =>
                  cn(
                    "flex cursor-pointer items-center gap-5 px-5 py-5 text-sm",
                    "hover:bg-action group",
                    "focus-visible:ring-control-ring-focus rounded-lg outline-none focus-visible:ring-[length:var(--control-ring-width-focus)]",
                    active && "bg-action",
                    checked && "bg-gray-100",
                  )
                }
                key={option.id}
                value={option}
              >
                {({ active, checked }) => (
                  <>
                    {option.icon && (
                      <RoundIconContainer
                        className={cn(
                          active && "bg-gray-100",
                          "group-hover:bg-gray-100",
                        )}
                      >
                        {option.icon}
                      </RoundIconContainer>
                    )}
                    <div className="flex-grow">
                      <h2 className="font-semibold">{option.name}</h2>
                      <div className="text-gray-600">
                        {renderOption(option)}
                      </div>
                    </div>
                    <div className="rounded-full border border-gray-800 p-1">
                      <div
                        className={cn(
                          "rounded-full p-1",
                          checked && "bg-gray-800",
                        )}
                      />
                    </div>
                  </>
                )}
              </RadioGroup.Option>
            ))}
          </RadioGroup>

          <Button
            className="mt-10 w-full"
            loading={submitting}
            vSize="lg"
            onClick={handleClick}
          >
            Next
          </Button>
        </BodyContainer>
      </div>
    </StepWizard.StepPageLayout>
  );
}
